import {useEffect, useRef} from 'react';
import {View, Text, StyleSheet, Animated} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {CircleCheck} from 'lucide-react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {useToast} from '@/contexts/ToastContext';

export const Toast = () => {
    const {toast} = useToast();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);

    const animValue = useRef(new Animated.Value(0)).current;
    const wasVisible = useRef(false);

    useEffect(() => {
        if (toast.visible) {
            wasVisible.current = true;
            Animated.timing(animValue, {
                toValue: 1,
                duration: 300,
                useNativeDriver: true,
            }).start();
        } else if (wasVisible.current) {
            Animated.timing(animValue, {
                toValue: 0,
                duration: 300,
                useNativeDriver: true,
            }).start(({finished}) => {
                if (finished) wasVisible.current = false;
            });
        }
    }, [toast.visible, animValue]);

    if (!toast.visible && !wasVisible.current) return null;

    const translateY = animValue.interpolate({
        inputRange: [0, 1],
        outputRange: [50, 0],
    });

    return (
        <Animated.View
            style={[
                styles.container,
                {opacity: animValue, transform: [{translateY}]},
            ]}
            pointerEvents="none"
        >
            <View style={styles.content}>
                <CircleCheck size={18} color={theme.colors.success} />
                <Text style={styles.message}>{toast.message}</Text>
            </View>
        </Animated.View>
    );
};

const createStyles = (theme: ThemeType, insets: {bottom: number}) =>
    StyleSheet.create({
        container: {
            position: 'absolute',
            bottom: insets.bottom + 16,
            left: 16,
            right: 16,
            zIndex: 20,
        },
        content: {
            backgroundColor: theme.colors.surface + 'F0',
            borderRadius: theme.borderRadius.lg,
            padding: 14,
            flexDirection: 'row',
            alignItems: 'center',
        },
        message: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.success,
            fontWeight: '600',
            marginLeft: 8,
        },
    });
